// ═══════════════════════════════════════════════════════════════
// HotWash — Observation Detail Modal
// View + Edit mode for a single observation
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import {
  Modal, Text, Badge, Group, Paper, Stack, Button, Divider,
  TextInput, Textarea, Select,
} from '../gab/GabComponents';
import { IconTrash, IconPencil, IconDeviceFloppy, IconX } from '@tabler/icons-react';
import type { Observation, ObservationTier, ObservationCategory, ObservationPhase } from '../../types';
import { TIER_META, TIER_KEYS, CATEGORIES, PHASES } from '../../types';
import { useAuthStore } from '../../store/authStore';
import { useHotWashStore } from '../../store/hotwashStore';

interface Props {
  observation: Observation | null;
  opened: boolean;
  onClose: () => void;
  onDelete?: (id: number) => void;
}

export default function ObservationModal({ observation, opened, onClose, onDelete }: Props) {
  const { incidents } = useAuthStore();
  const { updateObservation } = useHotWashStore();

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Edit state
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editTier, setEditTier] = useState<ObservationTier>('observation');
  const [editCategory, setEditCategory] = useState<ObservationCategory>('other');
  const [editPhase, setEditPhase] = useState<ObservationPhase>('response');
  const [editRemedy, setEditRemedy] = useState('');

  useEffect(() => {
    if (observation) {
      setEditTitle(observation.title);
      setEditDescription(observation.description);
      setEditTier(observation.tier);
      setEditCategory(observation.category);
      setEditPhase(observation.phase);
      setEditRemedy(observation.suggestedRemedy);
      setEditing(false);
    }
  }, [observation]);

  if (!observation) return null;

  const meta = TIER_META[observation.tier];
  const incident = incidents.find(i => String(i.id) === String(observation.incidentId));

  const tierOptions = TIER_KEYS.map(t => ({ value: t, label: TIER_META[t].label }));
  const catOptions = CATEGORIES.map(c => ({ value: c, label: c.charAt(0).toUpperCase() + c.slice(1) }));
  const phaseOptions = PHASES.map(p => ({ value: p, label: p.charAt(0).toUpperCase() + p.slice(1) }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateObservation(observation.id, {
        title: editTitle.trim(),
        description: editDescription.trim(),
        tier: editTier,
        category: editCategory,
        phase: editPhase,
        suggestedRemedy: editRemedy.trim(),
      });
      setEditing(false);
    } catch (err) {
      console.error('Failed to update observation:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setEditing(false);
    onClose();
  };

  return (
    <Modal opened={opened} onClose={handleClose} size="lg" title={null} padding="lg">
      <Stack gap="sm">
        {!editing ? (
          <>
            {/* View Mode */}
            <Group gap="xs">
              <Badge size="sm" style={{ background: meta.bg, color: meta.color, border: `1px solid ${meta.color}20` }}>
                {meta.label}
              </Badge>
              <Badge size="sm" variant="outline" color="#939598" tt="capitalize">{observation.category}</Badge>
              <Badge size="sm" variant="outline" color="#939598" tt="capitalize">{observation.phase}</Badge>
            </Group>

            <Text fw={600} fz="lg">{observation.title}</Text>

            <Group gap="lg">
              <div>
                <Text size="xs" c="dimmed">Submitted By</Text>
                <Text size="sm">{observation.isAnonymous ? 'Anonymous' : observation.submittedBy}</Text>
              </div>
              {incident && (
                <div>
                  <Text size="xs" c="dimmed">Incident</Text>
                  <Text size="sm">{incident.name}</Text>
                </div>
              )}
              <div>
                <Text size="xs" c="dimmed">Date</Text>
                <Text size="sm">
                  {observation.datecreated ? new Date(observation.datecreated).toLocaleDateString() : '—'}
                </Text>
              </div>
            </Group>

            <Divider />

            <div>
              <Text size="xs" c="dimmed" mb={4}>Description</Text>
              <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{observation.description || 'No description provided.'}</Text>
            </div>

            {observation.suggestedRemedy && (
              <Paper p="sm" radius="sm" bg="var(--success-light)" style={{ border: '1px solid var(--color-success)' }}>
                <Text size="xs" fw={600} c="var(--success)" mb={4}>Suggested Remedy</Text>
                <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{observation.suggestedRemedy}</Text>
              </Paper>
            )}

            <Divider />

            <Group justify="space-between">
              <Button variant="subtle" size="xs" leftSection={<IconPencil size={14} />} onClick={() => setEditing(true)}>
                Edit
              </Button>
              <Group gap="xs">
                {onDelete && (
                  <Button variant="subtle" color="var(--color-error)" size="xs" leftSection={<IconTrash size={14} />}
                    onClick={() => { onDelete(observation.id); handleClose(); }}>
                    Delete
                  </Button>
                )}
                <Button variant="default" size="xs" onClick={handleClose}>Close</Button>
              </Group>
            </Group>
          </>
        ) : (
          <>
            {/* Edit Mode */}
            <Text fw={600} size="sm">Edit Observation</Text>

            <TextInput label="Title" value={editTitle} onChange={e => setEditTitle(e.currentTarget.value)} required />

            <Textarea label="Description" value={editDescription} onChange={e => setEditDescription(e.currentTarget.value)}
              minRows={3} autosize />

            <Group grow>
              <Select label="Tier" data={tierOptions} value={editTier}
                onChange={v => setEditTier((v as ObservationTier) ?? 'observation')} />
              <Select label="Category" data={catOptions} value={editCategory}
                onChange={v => setEditCategory((v as ObservationCategory) ?? 'other')} />
              <Select label="Phase" data={phaseOptions} value={editPhase}
                onChange={v => setEditPhase((v as ObservationPhase) ?? 'response')} />
            </Group>

            <Textarea label="Suggested Remedy" value={editRemedy} onChange={e => setEditRemedy(e.currentTarget.value)}
              minRows={2} autosize />

            <Divider />

            <Group justify="flex-end">
              <Button variant="subtle" size="xs" leftSection={<IconX size={14} />}
                onClick={() => setEditing(false)}>Cancel</Button>
              <Button size="xs" leftSection={<IconDeviceFloppy size={14} />}
                onClick={handleSave} loading={saving}>Save Changes</Button>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
}
